import { openDocument } from '../helper/documentHelper';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Divider,
  Modal,
  Row,
  Typography,
  message,
} from 'antd';

import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Roles, StepName } from '../Types/interfaces';
import { GetInternshipProcessesToSign } from '../helper/apiHelper';
import { type UploadFile } from 'antd/lib';
import Dragger from 'antd/es/upload/Dragger';
import {
  DownloadOutlined,
  InboxOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { INTERNSHIP_APP_API } from '../Config/config';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useCurrentUser } from '../hooks/useCurrentUser';

type UploadeStatus = 'idle' | 'uploading' | 'success' | 'error';

type PendingDocument = {
  id: number;
  documentId: string;
  internshipName: string;
  studentName: string;
};

export default function FileUploader({
  doPaperWork,
  onFileUpload, // run when the file has been uploaded and sent
  role,
  apiUrl,
  documentUrl,
  documentSigningStatus,
  isSignatureDisabled,
  alreadySigned,
  isVisible = true,
}: Readonly<{
  doPaperWork?: () => Promise<void>;
  onFileUpload?: () => Promise<QueryObserverResult | void>;
  role: Roles;
  apiUrl: string;
  documentUrl?: string;
  documentSigningStatus?: StepName | string | Promise<string>;
  isSignatureDisabled: boolean;
  alreadySigned: boolean;
  isVisible?: boolean;
}>): React.ReactNode {
  const loggedInUser = useCurrentUser();
  const userId = loggedInUser?.id;
  const [files, setFiles] = useState<Array<File>>([]);
  const [status, setStatus] = useState<UploadeStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const { t } = useTranslation();

  const [fileInputRef, setFileInputRef] = useState<Array<UploadFile>>([]);
  const [openPdfWindow, setOpenPdfWindow] = useState<boolean>(false);

  const stepNamesByRole: Partial<Record<Roles, string>> = {
    [Roles.OrganizationRepresentative]:
      StepName.signingInternshipConventionByOrganization,
    [Roles.CadruDidactic]:
      StepName.signingInternshipConventionByUniversitySupervisor,
    [Roles.Rector]: StepName.signingInternshipConventionByRector,
    [Roles.Tutor]: StepName.signingInternshipConventionByTutor,
  };

  const stepName = stepNamesByRole[role];

  useEffect(() => {
    async function retrieveDocs(): Promise<void> {
      setStatus('success');
    }
    if (userId) {
      retrieveDocs();
    }
  }, [userId, role]);

  async function handleFileUpload(): Promise<void> {
    if (fileInputRef.length === 0) return;
    setStatus('uploading');
    setUploadProgress(0);
    const formData = new FormData();
    fileInputRef.forEach((file, index) => {
      if (file.originFileObj) {
        formData.append('file' + index.toString(), file.originFileObj);
      }
    });
    try {
      const url = `${INTERNSHIP_APP_API}/${apiUrl}${apiUrl.includes('research') ? '' : `?stepName=${stepName}`}`;
      const data = await axios.post<PendingDocument[]>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      if (!data || data.status !== 200) {
        throw new Error(t('convSignComp.uploadError'));
      }

      // Some upload endpoints (e.g. internship-processes-to-sign) return the list of
      // documents they actually processed - an empty array there means the uploaded
      // PDF wasn't recognized as signed. Other endpoints (e.g. collaboration-documents)
      // just return an empty 200 OK, which must NOT be treated as "nothing changed".
      if (Array.isArray(data.data) && data.data.length === 0) {
        throw new Error(t('convSignComp.noChangeDetected'));
      }

      if (doPaperWork) {
        await doPaperWork();
      }

      if (onFileUpload) {
        await onFileUpload();
      }

      setStatus('success');
      setUploadProgress(100);
      message.success(t('convSignComp.uploadSuccess'));
      setFiles([]);
    } catch (error) {
      setStatus('error');
      setUploadProgress(0);

      const backendMessage =
        axios.isAxiosError(error) &&
        error.response?.status === 400 &&
        typeof error.response?.data === 'string'
          ? error.response.data
          : undefined;

      const quotedFileName = backendMessage?.match(/'([^']+)'/)?.[1];

      if (backendMessage?.includes('exceeds the limit')) {
        message.error(t('convSignComp.fileTooLarge'));
      } else if (backendMessage?.includes('is missing a valid document ID')) {
        message.error(
          quotedFileName
            ? t('convSignComp.missingDocumentIdFile', { file: quotedFileName })
            : t('convSignComp.missingDocumentId')
        );
      } else if (
        backendMessage?.includes('does not match any known document') ||
        backendMessage?.includes("does not match this student's document")
      ) {
        message.error(
          quotedFileName
            ? t('convSignComp.documentMismatchFile', { file: quotedFileName })
            : t('convSignComp.documentMismatch')
        );
      } else if (backendMessage) {
        // Other backend validation errors are already human-readable, so show them as-is.
        message.error(backendMessage);
      } else if (!axios.isAxiosError(error) && error instanceof Error) {
        message.error(error.message);
      } else {
        message.error(t('convSignComp.uploadError'));
      }
      console.error(error);
    } finally {
      setFileInputRef([]);
      setOpenPdfWindow(false);
    }
  }

  const handleFileDownload = async (): Promise<void> => {
    if (documentUrl) {
      await openDocument(documentUrl, documentSigningStatus);
    } else {
      try {
        // file download
        const processes = await GetInternshipProcessesToSign(
          loggedInUser?.id ?? 0
        );
        // processes is a zip
        const blob = new Blob([processes], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'conventions.zip';
        link.click();
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
          message.error(t('fileUploadComp.noDocumentsToSign'));
        } else {
          message.error(t('fileUploadComp.error'));
        }
        console.error(error);
      }
    }
  };

  if (isVisible) {
    return (
      <>
        <Row
          justify="end"
          gutter={[16, 16]}
          wrap={true}
          style={{ marginTop: '8px' }}
        >
          <Col>
            <Button
              onClick={() => setOpenPdfWindow(true)}
              disabled={isSignatureDisabled}
              type="primary"
            >
              <PlusCircleOutlined />
              {documentUrl
                ? t('fileUploadComp.manageButtonSingular')
                : t('fileUploadComp.manageButtonPlural')}
              {}
            </Button>
          </Col>
        </Row>
        <Modal
          title={t('fileUploadComp.title')}
          open={openPdfWindow}
          okText={t('fileUploadComp.uploadButton')}
          cancelText={t('fileUploadComp.cancel')}
          onOk={async () => {
            await handleFileUpload();
          }}
          onCancel={() => {
            setFileInputRef([]);
            setOpenPdfWindow(false);
          }}
        >
          <Divider />
          {alreadySigned && (
            <Alert message={t('fileUploadComp.reuploadInfo')} type="info" />
          )}
          <Button
            style={{ margin: 16, marginLeft: 0 }}
            type="primary"
            onClick={handleFileDownload}
          >
            <DownloadOutlined />
            {t('fileUploadComp.download')}
          </Button>
          <Divider />

          <Dragger
            fileList={fileInputRef}
            accept={documentUrl ? '.pdf' : '.pdf,.zip'}
            beforeUpload={() => false}
            maxCount={documentUrl ? 1 : 0}
            multiple={documentUrl === undefined}
            onChange={(item) => {
              setFileInputRef(item.fileList);
            }}
          >
            <InboxOutlined style={{ fontSize: '50px' }} />
            <br />
            <Typography.Text>
              {documentUrl
                ? t('fileUploadComp.infoTextPdf')
                : t('fileUploadComp.infoTextPdfZip')}
            </Typography.Text>
          </Dragger>
          <Divider />
          <Row justify="end" style={{ marginBottom: '5px' }}>
            {files.length !== 0 && status !== 'uploading' && (
              <Button type="primary" onClick={handleFileUpload}>
                {t('convSignComp.uploadSigned')}
              </Button>
            )}
          </Row>
          <Row justify="end" style={{ marginBottom: '10px' }}>
            {status === 'uploading' && (
              <div style={{ marginTop: '10px' }}>
                <div
                  style={{
                    height: '10px',
                    width: '100%',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '999px',
                  }}
                >
                  <div
                    style={{
                      height: '10px',
                      width: `${uploadProgress}%`,
                      backgroundColor: '#2563eb',
                      transition: 'all 0.3s ease-in-out',
                      borderRadius: '999px',
                    }}
                  ></div>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {uploadProgress}% uploaded
                </p>
              </div>
            )}
          </Row>
        </Modal>
      </>
    );
  }
}
